import { useId } from 'react';
import { t } from '../../lib/i18n';
import { formatDateTime } from '../../lib/format';

export interface Point {
  /** ISO time of the hour. */
  at: string;
  value: number | null;
}

const W = 600;
const H = 140;
const PAD = { left: 36, right: 8, top: 8, bottom: 22 };

function dayLabel(ms: number): string {
  return formatDateTime(new Date(ms).toISOString()).split(',')[0] ?? '';
}

/**
 * A small inline-SVG chart of hourly values between `from` and `to`. `bars` suit counts (minutes online,
 * sign-ins); `line` suits levels (battery, Wi-Fi) and leaves a gap where an hour has no reading.
 * The figure has a text title and summary so it does not rely on the picture alone.
 */
export function HourChart({
  title,
  summary,
  points,
  from,
  to,
  max,
  kind,
}: {
  title: string;
  summary: string;
  points: Point[];
  from: string;
  to: string;
  max: number;
  kind: 'bars' | 'line';
}) {
  const id = useId();
  const start = new Date(from).getTime();
  const end = Math.max(new Date(to).getTime(), start + 1);
  const innerW = W - PAD.left - PAD.right;
  const innerH = H - PAD.top - PAD.bottom;
  const x = (iso: string): number => PAD.left + ((new Date(iso).getTime() - start) / (end - start)) * innerW;
  const y = (v: number): number => PAD.top + innerH - (Math.min(Math.max(v, 0), max) / max) * innerH;
  const barW = Math.max(1, innerW / Math.max(1, (end - start) / 3_600_000) - 0.5);

  let path = '';
  if (kind === 'line') {
    let pen = false;
    for (const p of points) {
      if (p.value === null) {
        pen = false;
        continue;
      }
      path += `${pen ? 'L' : 'M'}${x(p.at).toFixed(1)} ${y(p.value).toFixed(1)} `;
      pen = true;
    }
  }

  return (
    <figure className="m-0">
      <figcaption className="mb-1 text-sm font-medium text-slate-900">{title}</figcaption>
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-labelledby={`${id}-t ${id}-d`} className="h-auto w-full text-brand">
        <title id={`${id}-t`}>{title}</title>
        <desc id={`${id}-d`}>{summary}</desc>
        {[0, 0.5, 1].map((f) => (
          <g key={f}>
            <line x1={PAD.left} x2={W - PAD.right} y1={y(max * f)} y2={y(max * f)} stroke="#e2e8f0" strokeWidth="1" />
            <text x={PAD.left - 4} y={y(max * f) + 3} textAnchor="end" fontSize="10" fill="#475569">
              {Math.round(max * f)}
            </text>
          </g>
        ))}
        {kind === 'bars'
          ? points.map((p) =>
              p.value === null || p.value <= 0 ? null : (
                <rect key={p.at} x={x(p.at)} y={y(p.value)} width={barW} height={PAD.top + innerH - y(p.value)} fill="currentColor" />
              ),
            )
          : null}
        {kind === 'line' && path ? <path d={path} fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" /> : null}
        <text x={PAD.left} y={H - 6} fontSize="10" fill="#475569">
          {t('devicesUi.chart.from', { date: dayLabel(start) })}
        </text>
        <text x={W - PAD.right} y={H - 6} fontSize="10" textAnchor="end" fill="#475569">
          {t('devicesUi.chart.to', { date: dayLabel(end) })}
        </text>
      </svg>
      <p className="text-xs text-slate-600">{summary}</p>
    </figure>
  );
}
